<template>
  <div class="counselor-contact-form contact-form">
    <transition mode="out-in" name="contact-form__fade">
      <form
        method="post"
        v-if="!formSent"
        :name="formName"
        @submit.prevent="submitForm()"
      >
        <transition name="contact-form__reveal-animation">
          <case-type-select
            v-if="showCaseTypeSelect"
            :options="options"
            :request-type="requestType"
            @update-case-type="onUpdateCaseType"
          />
        </transition>

        <meeting-type
          class="form-row"
          v-if="!disableMeetingTypeContactForm"
          v-model:meeting-type="meetingType"
          :available-meeting-types="availableMeetingTypes"
          :request-type="requestType"
        />

        <meeting-length
          class="form-row"
          v-model:price="price"
          :meeting-type="meetingType"
          :prices="formattedPrices"
          :request-type="requestType"
        />

        <transition name="contact-form__reveal-animation">
          <time-slot-picker
            class="form-row"
            v-if="showTimepickerAndMeetingBundleSelect"
            :is-counselor-payment-form="true"
            :meeting-price="price"
            :meeting-type="meetingType"
            :price="price"
            :request-type="requestType"
            :set-time="setTime"
            :set-timezone="setTimezone"
          />
        </transition>

        <transition name="contact-form__reveal-animation">
          <meeting-bundle-select
            v-if="showTimepickerAndMeetingBundleSelect"
            v-model:meeting-count="meetingCount"
            :bundles-meta="bundlesMeta"
            :has-error="hasMeetingCountError"
            :meeting-type="meetingType"
            :office-prices-list="officePricesMeta"
            :price-meta-key="price"
            :prices-list="pricesMeta"
            :request-type="requestType"
          />
        </transition>

        <transition name="contact-form__reveal-animation">
          <payment-form-usps
            v-if="showTimepickerAndMeetingBundleSelect"
            :only-show-first="true"
          />
        </transition>

        <transition name="contact-form__reveal-animation">
          <contact-info
            v-if="showContactInfo"
            ref="contactInfo"
            v-model:name="name"
            v-model:email="email"
            v-model:phone="phone"
            v-model:message="message"
            :message-is-required="messageIsRequired"
            @trigger-field-error="triggerFieldError"
          />
        </transition>

        <transition name="contact-form__reveal-animation">
          <coupon-code
            v-if="showCouponCode"
            v-model:coupon="coupon"
            :check-coupon="checkCouponWrapper"
            :valid-coupon="validCoupon"
            @trigger-field-error="triggerFieldError"
          />
        </transition>

        <submit-section
          v-model:accept="accept"
          :accept-text="contactTexts.contactFormAcceptTextStripeConnect"
          :error="error"
          :has-errors="hasErrors"
          :is-disabled="isDisabled"
          :loading="contactFormIsLoading"
          :show-accept-text="showAcceptText"
          :submit-button-text="submitButtonText"
        />

        <payment-form-usps />
      </form>

      <form-success-message
        v-else
        :counselor-form="true"
        :name="name"
        :request-type="requestType"
        :selected-slot="selectedSlot"
      />
    </transition>

    <loader :loading="contactFormIsLoading || isLoading" />
  </div>
</template>

<script>
import Loader from 'Components/loader/Loader.vue';
import { useContactForm } from 'Composables/useContactForm';
import { useCityTermLocalStorage } from 'Main/composables/useCityTermLocalStorage';
import { useUser } from 'Main/composables/useUser';
import CaseTypeSelect from 'Main/contact-form/components/CaseTypeSelect.vue';
import ContactInfo from 'Main/contact-form/components/ContactInfo.vue';
import CouponCode from 'Main/contact-form/components/CouponCode.vue';
import FormSuccessMessage from 'Main/contact-form/components/FormSuccessMessage.vue';
import MeetingBundleSelect from 'Main/contact-form/components/MeetingBundleSelect.vue';
import MeetingLength from 'Main/contact-form/components/MeetingLength.vue';
import MeetingType from 'Main/contact-form/components/MeetingType.vue';
import PaymentFormUsps from 'Main/contact-form/components/PaymentFormUsps.vue';
import SubmitSection from 'Main/contact-form/components/SubmitSection.vue';
import TimeSlotPicker from 'Main/contact-form/components/TimeSlotPicker.vue';
import {
  DEFAULT_ROLES,
  FIELD_ORDER,isTherapyRequest,
  MEETING_BUNDLE_COUNT, 
  MEETING_TYPE_KEYS,
  REQUEST_TYPES,
  ROLES,
} from 'Main/contact-form/constants';
import { mapMutations, mapState } from 'vuex';

export default {

  components: {
    CaseTypeSelect,
    ContactInfo,
    CouponCode,
    FormSuccessMessage,
    Loader,
    MeetingBundleSelect,
    MeetingLength,
    MeetingType,
    PaymentFormUsps,
    SubmitSection,
    TimeSlotPicker,
  },

  computed: {
    ...mapState('cities', ['cities', 'cityLinks']),
    ...mapState('user', [
      'caseTypes',
      'schedule',
      'isLoading',
      'role',
      'locations',
      'allowPhysicalMeetings',
    ]),

    additionalFormData() {
      return { wpAdvisorId: this.wpAdvisorId };
    },

    availableMeetingTypes() {
      if (!this.isLoading) {
        // Decople array from state.haxxxxx
        const userSchedule = JSON.parse(JSON.stringify(this.schedule));
        if (
          this.hasOfficePrices &&
          this.allowPhysicalMeetings &&
          (!userSchedule || this.hasAllMeetingsSlots(userSchedule))
        ) {
          return {
            [MEETING_TYPE_KEYS.VIDEO]: this.texts.videoMeeting,
            [MEETING_TYPE_KEYS.AT_OUR_OFFICES]: this.texts.meetingOurOffice,
          };
        }
      }

      return { [MEETING_TYPE_KEYS.VIDEO]: this.texts.videoMeeting };
    },

    contactFormIsLoading() {
      return this.$store.state.contactForm.isLoading;
    },

    formattedPrices() {
      return this.getFormattedPrices();
    },

    formName() {
      return `contact-form-${this.requestType}-advisor-page`;
    },

    hasNoCityTermOrCities() {
      return !(this.cityTerm && this.cities);
    },

    hasOfficePrices() {
      return this.officePrices && Object.keys(this.officePrices).length > 0;
    },

    isDisabled() {
      if (!this.showContactInfo) {
        return true;
      }

      if (this.showCaseTypeSelect && this.caseType === '') {
        return true;
      }

      if (
        this.showCaseTypeSelect &&
        this.caseType === 'Annat' &&
        !this.message.length
      ) {
        return true;
      }

      if (this.meetingType === '') {
        return true;
      }

      if (this.price === null) {
        return true;
      }

      return !(this.requestType && this.name && this.phone && this.accept);
    },

    isLegal() {
      return this.requestType === REQUEST_TYPES.LAW;
    },

    isTherapy() {
      return isTherapyRequest(this.requestType);
    },

    messageIsRequired() {
      return this.requestType === REQUEST_TYPES.LAW;
    },

    missingFields() {
      const requiredFields = ['name', 'phone', 'accept', 'requestType'];

      if (this.showCaseTypeSelect) {
        requiredFields.push('caseType');

        if (this.caseType === 'Annat') {
          requiredFields.push('message');
        }
      }

      if (this.isTherapy) {
        requiredFields.push('price', 'email', 'meetingType');
      }

      return requiredFields
        .filter((field) => !this[field])
        .sort((a, b) => FIELD_ORDER.indexOf(a) - FIELD_ORDER.indexOf(b));
    },

    options() {
      if (!(this.caseTypes && this.showCaseTypeSelect)) {
        return [];
      }

      const typeToFilterOn = this.isTherapy ? 'THERAPY' : 'LEGAL';

      return this.caseTypes
        .filter(({ type }) => type === typeToFilterOn)
        .map((item) => ({ label: item.name, value: item.slug }));
    },

    parentCity() {
      if (this.hasNoCityTermOrCities) {
        return null;
      }

      const city = this.cities.find(
        (city) => city.label === this.cityTerm.name
      );

      return city ? city.parent : null;
    },

    priceChoices() {
      if (
        this.meetingType === MEETING_TYPE_KEYS.AT_OUR_OFFICES &&
        this.hasOfficePrices
      ) {
        return this.officePrices || {};
      }

      return this.prices || {};
    },

    requestType: {
      get() {
        return this.role;
      },
      set(newType) {
        this.setRole(newType);
      },
    },

    selectedCity() {
      if (!this.cityTerm) {
        return null;
      }

      return this.cityTerm.name;
    },

    showAcceptText() {
      if (this.isTherapy) {
        return this.showContactInfo;
      }
      return true;
    },

    showCaseTypeSelect() {
      return this.isLegal || this.isTherapy;
    },

    showContactInfo() {
      return this.showTimepickerAndMeetingBundleSelect && this.meetingCount > 0;
    },

    showCouponCode() {
      if (
        this.currentSite.toUpperCase() === 'DK' ||
        !this.isTherapy
      ) {
        return false;
      }
      return this.showContactInfo;
    },

    showTimepickerAndMeetingBundleSelect() {
      return (
        this.price !== null &&
        Array.isArray(this.schedule) &&
        this.schedule.length > 0
      );
    },

    submitButtonText() {
      return this.isDisabled
        ? this.contactTexts.bookMeeting
        : this.contactTexts.submitPayment;
    },
  },

  created() {
    this.maybeFetchUser(this.advisorId, this.schedule.length, this.timezone);
  },

  data() {
    const {
      advisorId,
      advisorEmail,
      pageTitle = '',
      defaultRole,
      prices,
      officePrices,
      wpAdvisorId,
      pricesMeta,
      officePricesMeta,
      competencePriceInfo,
      competenceOfficePriceInfo,
      bundlesMeta,
    } = this.$mountEl.dataset;

    return {
      advisorEmail,
      advisorId,
      bundlesMeta: JSON.parse(bundlesMeta),
      competenceOfficePriceInfo: JSON.parse(competenceOfficePriceInfo || '{}'),
      competencePriceInfo: JSON.parse(competencePriceInfo || '{}'),
      contactTexts: theme_vars.contactTexts,
      contactVia: pageTitle,
      coupon: '',
      defaultRole,
      disableCitySelectContactForm: theme_vars.disableCitySelectContactForm,
      disableMeetingTypeContactForm: theme_vars.disableMeetingTypeContactForm,
      formSent: false,
      isCounselorPaymentForm: true,
      meetingCount: MEETING_BUNDLE_COUNT,
      meetingType: MEETING_TYPE_KEYS.VIDEO,
      officePrices: JSON.parse(officePrices),
      officePricesMeta: JSON.parse(officePricesMeta),
      price: null,
      prices: JSON.parse(prices),
      pricesMeta: JSON.parse(pricesMeta),
      selectedSlot: null,
      showCouponInput: false,
      texts: theme_vars.counselorContactFormTexts,
      timeslots: [],
      validate: ['name', 'phone', 'email'],
      wpAdvisorId,
    };
  },

  methods: {
    ...mapMutations('user', ['setRole', 'setAvailableMeetingTypesTherapy']),

    checkCouponWrapper() {
      return this.checkCoupon(
        this.coupon,
        this.advisorId,
        this.email,
        this.contactTexts
      );
    },

    getCompetenceFormattedPrices() {
      const source =
        this.meetingType === MEETING_TYPE_KEYS.AT_OUR_OFFICES
          ? this.competenceOfficePriceInfo
          : this.competencePriceInfo;
      const byCase = source && source[this.caseType];
      return byCase && Object.keys(byCase).length > 0 ? byCase : null;
    },

    getDefaultFormattedPrices() {
      if (this.meetingType === MEETING_TYPE_KEYS.AT_OUR_OFFICES) {
        return this.officePrices || {};
      }

      return this.prices || {};
    },

    getFormattedPrices() {
      if (!this.caseType || this.caseType === '' || this.caseType === 'Annat') {
        return this.getDefaultFormattedPrices();
      }

      const competencePricing = this.getCompetenceFormattedPrices();
      if (competencePricing) {
        return competencePricing;
      }

      return this.getDefaultFormattedPrices();
    },

    onUpdateCaseType(val) {
      const newPriceKey = this.updateCaseType(val, {
        getFormattedPrices: () => this.getFormattedPrices(),
        price: this.price,
        requestType: this.requestType,
      });
      if (newPriceKey !== null) {
        this.price = newPriceKey;
      }
    },

    setMeetingTypes() {
      const availableMeetingTypes = this.availableMeetingTypes;

      if (!availableMeetingTypes) {
        setTimeout(() => this.setMeetingTypes(), 100);
        return;
      }

      if (Object.keys(availableMeetingTypes).length === 1) {
        this.meetingType = MEETING_TYPE_KEYS.VIDEO;
      }
    },

    setTime(selectedSlot) {
      if (!selectedSlot) {
        this.selectedSlot = null;
        return;
      }

      const { date, timeslot } = selectedSlot;

      if (!timeslot) {
        this.selectedSlot = null;
        return;
      }

      this.selectedSlot = {
        end_date: `${date} ${timeslot.endTime}`,
        start_date: `${date} ${timeslot.startTime}`,
      };
    },

    setTimezone(timezone) {
      this.timezone = timezone;

      if (!this.timezone) {
        return;
      }

      this.getUpdatedSchedule(this.advisorId, this.timezone);
    },

    async submitForm() {
      await this.sendInquiry({
        additionalFormData: this.additionalFormData,
        advisorEmail: this.advisorEmail,
        advisorId: this.advisorId,
        contactInfoRef: this.$refs.contactInfo,
        contactVia: this.contactVia,
        coupon: this.coupon,
        formName: this.formName,
        formSent: this.formSent,
        isCounselorPaymentForm: this.isCounselorPaymentForm,
        meetingCount: this.meetingCount,
        meetingType: this.meetingType,
        missingFields: this.missingFields,
        onFormSent: (val) => {
          this.formSent = val;
        },
        price: this.price,
        refs: this.$refs,
        requestType: this.requestType,
        rootEl: this.$mountEl,
        selectedCity: this.selectedCity,
        selectedSlot: this.selectedSlot,
        showMeetingBundleSelect: this.showTimepickerAndMeetingBundleSelect,
        texts: this.contactTexts,
        validateRefs: this.validate,
      });
    },
  },

  mounted() {
    if (this.defaultRole && !this.role) {
      switch (this.defaultRole) {
        case DEFAULT_ROLES.LAW:
          this.setRole(ROLES.LAW);
          break;
        case DEFAULT_ROLES.THERAPY:
          this.setRole(ROLES.THERAPY);
          break;
        default:
          this.setRole(ROLES.FUNERAL);
          break;
      }
    }

    this.setMeetingTypes();
  },
  name: 'CounselorPaymentForm',

  setup() {
    const { maybeFetchUser, getUpdatedSchedule } = useUser();
    const {
      cityTerm,
      saveCityTermToLocalStorage,
      getCityTermFromLocalStorage,
    } = useCityTermLocalStorage();
    const contactForm = useContactForm();
    return {
      cityTerm,
      getCityTermFromLocalStorage,
      getUpdatedSchedule,
      maybeFetchUser,
      saveCityTermToLocalStorage,
      ...contactForm,
    };
  },

  watch: {

    availableMeetingTypes(availableMeetingTypes) {
      this.setAvailableMeetingTypesTherapy(availableMeetingTypes);
    },
    cityLinks() {
      this.cityTerm = this.getCityTermFromLocalStorage();
    },

    meetingCount(meetingCount) {
      if (meetingCount > 0) {
        this.error = '';
        this.hasErrors = false;
        this.hasFieldError = false;
        this.hasMeetingCountError = false;
      }
    },

    meetingType() {
      this.price = null;
      this.selectedSlot = null;
    },

    price() {
      this.selectedSlot = null;
    },
  },
};
</script>
